import { PORTFOLIO_TYPE_CONSTS } from '@modules/edit-preference/components/portfolio/components/add-edit-portfolio-type/add-edit-portfolio-type.fields';

export interface PortfolioShareTab {
  label: string;
  key: string;
  iconClass?: string;
}

export interface PortfolioShareOption {
  id: string;
  title: string;
  thumbnail: string;
  portfolio_type: string;
  description?: string;
  sk?: string;
  pk?: string;
  enable?: boolean;
}

export interface PortfolioContentItem {
  id: string;
  title: string;
  thumbnail: string;
  sk?: string;
  pk?: string;
  type?: string;
  description?: string;
  portfolioSk?: string;
  isIndividual?: boolean;
  releaseDate?: string;
}

/** Tab key for "All" tab (used in comparisons) */
export const PORTFOLIO_SHARE_TAB_KEY_ALL = 'all';

export const PORTFOLIO_SHARE_TABS: PortfolioShareTab[] = [
  {
    label: 'ALL',
    key: PORTFOLIO_SHARE_TAB_KEY_ALL,
  },
  {
    label: 'Music',
    key: PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC,
    iconClass: 'icon-litemusic-node-2',
  },
  {
    label: 'Podcasts',
    key: PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST,
    iconClass: 'icon-liteMIc-Icon',
  },
  {
    label: 'Videos',
    key: PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO,
    iconClass: 'icon-litevideo-fill-camera',
  },
  {
    label: 'Custom',
    key: PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM,
  },
];

export const SUB_TAB_ALBUMS = 'albums';
export const SUB_TAB_INDIVIDUAL = 'individual';

export const SUB_TAB_LABELS: {
  albums: { [portfolioType: string]: string };
  individual: { [portfolioType: string]: string };
} = {
  albums: {
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'Albums',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'Video Series',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'Podcast Series',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'Collections',
  },
  individual: {
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'Single Songs',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'Single Videos',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'Single Episodes',
    [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'Single Items',
  },
};

export const PORTFOLIO_SHARE_LABELS = {
  CANCEL: 'CANCEL',
  ADD: 'ADD',
  LOADING: 'Loading...',
  NOT_FOUND: 'No Results Found',
  SELECT_PORTFOLIO: 'Select Portfolio Items',
  SELECT_PORTFOLIO_ITEM: 'Select Portfolio Item',
  SEARCH: 'Search',
  BACK: 'Back',
  ALBUMS: 'Albums',
  INDIVIDUAL: 'Individual',
  NO_PORTFOLIOS: 'No portfolios found',
  NO_ALBUMS: 'No albums found',
  NO_INDIVIDUAL: 'No individual items found',
  NO_CONTENT_FOR_TYPE: 'No content available for this portfolio type',
  FAILED_TO_LOAD: 'Failed to load portfolios',
  VIEW_ITEMS: 'View Items',
  FEATURE: 'FEATURE'
};

export const NO_PORTFOLIOS_BY_TYPE: { [tabKey: string]: string } = {
  [PORTFOLIO_SHARE_TAB_KEY_ALL]: 'No portfolios found',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'No music portfolios found',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'No podcast portfolios found',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'No video portfolios found',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'No custom portfolios found',
};

export const PORTFOLIO_SHARE_CONSTS = {
  ZERO: 0,
  FIRST_TAB_INDEX: 0,
  MIN_TAB_CONTENT_COUNT: 1,
  SCROLLBAR_HEIGHT: 360,
  CACHE_EXPIRY_MS: 6 * 60 * 60 * 1000,
  ICON_PORTFOLIO_DEFAULT: 'icon-liteportfolio',
  ICON_PORTFOLIO_MUSIC: 'icon-litemusic-node-2',
  ICON_PORTFOLIO_PODCAST: 'icon-liteMIc-Icon',
  ICON_PORTFOLIO_VIDEO: 'icon-litevideo-fill-camera',
  ICON_PORTFOLIO_CUSTOM: 'icon-liteportfolio',
};

export const PORTFOLIO_TYPE_ICONS: { [portfolioType: string]: string } = {
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: PORTFOLIO_SHARE_CONSTS.ICON_PORTFOLIO_MUSIC,
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: PORTFOLIO_SHARE_CONSTS.ICON_PORTFOLIO_PODCAST,
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: PORTFOLIO_SHARE_CONSTS.ICON_PORTFOLIO_VIDEO,
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: PORTFOLIO_SHARE_CONSTS.ICON_PORTFOLIO_DEFAULT,
};

/** CTA labels for individual items (single song, episode, video) */
export const PORTFOLIO_CTA_INDIVIDUAL: { [key: string]: string } = {
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'LISTEN MUSIC',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'LISTEN TO PODCAST',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'WATCH VIDEO',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'VIEW ITEM',
};

/** CTA labels for collections (album, podcast series, video series) */
export const PORTFOLIO_CTA_COLLECTION: { [key: string]: string } = {
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'VIEW ALBUM',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'VIEW PODCAST SERIES',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'VIEW VIDEO SERIES',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'VIEW COLLECTION',
};

/** CTA labels for Phase 3: "View Album Songs" / "View Podcast Episodes" / "View Video Series" */
export const VIEW_PHASE3_CTA: { [portfolioType: string]: string } = {
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.MUSIC]: 'View Album Songs',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.PODCAST]: 'View Podcast Episodes',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.VIDEO]: 'View Video Series',
  [PORTFOLIO_TYPE_CONSTS.PORTFOLIO_TYPE.CUSTOM]: 'View Items',
};

/** Reverse lookup: button text -> { portfolioType, isIndividual } for restoring placeholders from email HTML */
export const PORTFOLIO_CTA_BUTTON_TO_TYPE: { [buttonText: string]: { portfolioType: string; isIndividual: boolean } } = (() => {
  const map: { [buttonText: string]: { portfolioType: string; isIndividual: boolean } } = {};
  Object.entries(PORTFOLIO_CTA_INDIVIDUAL).forEach(([type, label]) => {
    map[label] = { portfolioType: type, isIndividual: true };
  });
  Object.entries(PORTFOLIO_CTA_COLLECTION).forEach(([type, label]) => {
    if (!map[label]) map[label] = { portfolioType: type, isIndividual: false };
  });
  return map;
})();
